import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Library, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const LANG = { "en-US": "EN", "es-US": "ES", "ht-HT": "HT" };

function Row({ f, onPull, busy }) {
  return (
    <li className="py-3 flex items-start gap-3" data-testid={`library-row-${f.family_id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{f.name}</p>
        <p className="text-xs text-slate-500 mt-0.5">{f.purpose} - {[...f.languages].sort((a, b) => (a.language === "en-US" ? -1 : b.language === "en-US" ? 1 : a.language.localeCompare(b.language))).map((l) => `${LANG[l.language] || l.language} v${l.version}${l.org_version ? ` (you have v${l.org_version})` : ""}`).join(", ")}</p>
      </div>
      {!f.in_org && <Button size="sm" className="btn-brand" disabled={busy} onClick={() => onPull(f, false)} data-testid={`library-add-${f.family_id}`}>Add</Button>}
      {f.in_org && f.newer && <Button size="sm" variant="outline" className="border-amber-400 text-amber-800" disabled={busy} onClick={() => onPull(f, true)} data-testid={`library-newer-${f.family_id}`}>Get newer version</Button>}
      {f.in_org && !f.newer && <span className="text-xs text-emerald-700 mt-1.5" data-testid={`library-uptodate-${f.family_id}`}>In your forms</span>}
    </li>
  );
}

export default function LibraryDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/templates/library").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (open) { load(); } }, [open]);
  const pull = async (f, newer) => {
    if (newer && !window.confirm(`Bring in the newer library version of "${f.name}"?\n\nIt arrives as a DRAFT new version in your forms. Your current version stays in use until a template approver approves and publishes the draft. Any changes you made to your copy are not carried over.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/templates/library/${f.family_id}/pull`); toast.success(data.added ? `${f.name} added to your forms` : `New draft version of ${f.name} created - a template approver must publish it`); load(); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const shown = rows.filter((f) => `${f.name} ${f.purpose}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="library-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Library className="w-5 h-5" />Select from shared library</DialogTitle>
          <DialogDescription>Approved master forms. Adding one copies it into your organization; later library changes never reach you unless you choose "Get newer version".</DialogDescription></DialogHeader>
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search library forms" className="pl-8 h-9" data-testid="library-search" /></div>
        <ul className="divide-y max-h-[55vh] overflow-y-auto">{shown.map((f) => <Row key={f.family_id} f={f} onPull={pull} busy={busy} />)}
          {!shown.length && <li className="py-6 text-center text-sm text-slate-500">No library forms match.</li>}</ul>
      </DialogContent>
    </Dialog>
  );
}
